import { useEffect, useState } from 'react';
import type { CampistaTribo, TriboResumo } from './types';
import { ROTULO_SITUACAO } from './types';

interface Props {
  campista: CampistaTribo;
  tribos: TriboResumo[];
  aoMover: (inscricaoId: number, triboId: number) => void;
  aoConfirmar: (campista: CampistaTribo) => void;
  aoTirar: (campista: CampistaTribo) => void;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0][0] ?? '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] ?? '' : '';
  return `${primeira}${ultima}`.toUpperCase();
}

export default function LinhaCampista({
  campista: c,
  tribos,
  aoMover,
  aoConfirmar,
  aoTirar,
}: Props) {
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    if (!menu) return;
    const fechar = () => setMenu(false);
    window.addEventListener('click', fechar);
    return () => window.removeEventListener('click', fechar);
  }, [menu]);

  const situacao = ROTULO_SITUACAO[c.situacao] ?? c.situacao;
  const suplente = c.situacao === 'lista_espera';
  const local = c.cidade || c.grupo || 'Sem cidade informada';
  const sugerida = c.status_tribo === 'sugerida';
  const outras = tribos.filter((t) => t.tribo_id !== c.tribo_id);

  return (
    <div className="border-b border-background-200/70 px-3 py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-100 text-[13px] font-bold text-foreground-700">
          {iniciais(c.nome)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[14px] font-semibold text-foreground-950">{c.nome}</span>
            {sugerida && (
              <span className="rounded-full border border-dashed border-accent-400 bg-accent-50 px-2 py-[1px] text-[11px] font-semibold text-accent-800">
                Sugerida
              </span>
            )}
            {c.status_tribo === 'confirmada' && (
              <span className="rounded-full bg-secondary-100 px-2 py-[1px] text-[11px] font-semibold text-secondary-900">
                Confirmada
              </span>
            )}
            {suplente && (
              <span className="rounded-full bg-background-200 px-2 py-[1px] text-[11px] font-semibold text-foreground-800">
                Suplente
              </span>
            )}
          </div>

          <p className="mt-0.5 text-[12.5px] text-foreground-600">
            {c.sexo === 'M' ? 'Masculino' : c.sexo === 'F' ? 'Feminino' : 'Sexo não informado'}
            {c.idade !== null ? ` · ${c.idade} anos` : ''} · {local}
          </p>

          <p className="mt-0.5 text-[12px] text-foreground-500">Situação: {situacao}</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {sugerida && c.tribo_id !== null && (
            <button
              type="button"
              onClick={() => aoConfirmar(c)}
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Confirmar
            </button>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenu((v) => !v);
              }}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
            >
              Mover para…
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-down-s-line text-[13px]" aria-hidden />
              </span>
            </button>

            {menu && (
              <div className="absolute right-0 top-full z-30 mt-1 w-52 overflow-hidden rounded-2xl border border-background-200 bg-white shadow-lg">
                <div className="max-h-56 overflow-y-auto">
                  {outras.length === 0 && (
                    <p className="px-3 py-3 text-[12.5px] text-foreground-500">
                      Nenhuma outra tribo disponível.
                    </p>
                  )}
                  {outras.map((t) => (
                    <button
                      key={t.tribo_id}
                      type="button"
                      onClick={() => {
                        setMenu(false);
                        aoMover(c.inscricao_id, t.tribo_id);
                      }}
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-[13px] text-foreground-900 transition-colors hover:bg-background-100"
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: t.cor ?? '#94A3B8' }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">{t.nome}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {c.tribo_id !== null && (
            <button
              type="button"
              onClick={() => aoTirar(c)}
              aria-label={`Tirar ${c.nome} da tribo`}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-white text-foreground-600 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line text-[15px]" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}